import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { BurnBar } from '../components/BurnBar'
import { StatCard } from '../components/StatCard'

/** `GET /api/status/overview` (`backend/src/routes/status.ts`). */
interface ServiceStatus {
  service: string
  running: number
  desired: number
  rolloutState?: string
  image?: string | null
}
interface AlarmDetail {
  name: string
  reason: string | null
  metric: string | null
  namespace: string | null
  since: string | null
}
interface StatusOverview {
  services: ServiceStatus[]
  alarms: AlarmDetail[]
  /** ARNs `ListServices` returned in total — the denominator for `services`. */
  serviceCount?: number
  /** False when the ARN list was not read to completion. */
  listComplete?: boolean
  listError?: string | null
  todaySpend: number
  platformCeiling: number | null
  activeUsers: number
  /** Chat vs Claude Code, resolved via spendIdentity.ts's SPEND_SURFACE_IS_CODE
   *  off the same key-owner roster `activeUsers` itself is deduped through.
   *  Only meaningful when `keyOwnersAvailable` is true -- see that field. */
  activeUsersChat?: number
  activeUsersDeveloper?: number
  /** False when the gateway key roster could not be read for this render, in
   *  which case every row reads as chat and the chat/developer split above is
   *  not a real reading -- state it rather than show a developer count of
   *  zero as if nobody had written code today. */
  keyOwnersAvailable?: boolean
  activeUsersWindow?: string
}

/** `GET /api/status/mcp` (`backend/src/routes/status.ts`), reusing
 *  `loadMcpBoard` -- the same nightly-warehouse reader the full `/board`
 *  Tools view reads. Denials here are ACCESS DENIALS AT THE INGRESS: a
 *  query the SQL gate rejects never reaches `chugpt_usage`'s emitter, so it
 *  is not counted here at all -- see the tile's own note on screen. */
interface McpTileServer {
  server: string
  displayName: string
  calls: number
  denials: number
}
interface McpTileReport {
  available: boolean
  windowDays: number
  servers: McpTileServer[]
  totalCalls: number
  /** The GLOBAL count, off every decision row -- never a sum of the
   *  (possibly capped) per-server list above. */
  totalDenials: number
  /** True when the per-server denial list hit its own cap, so the split by
   *  server could undercount even though `totalDenials` above never does. */
  denialsCapped: boolean
}

/** `GET /api/status/data-health` — the schema-drift detector's latest result. */
interface DataHealth {
  missingViews: string[]
  unexpectedViews: string[]
  lastCheck: string | null
  stale: boolean
  /** Non-null when the detector's log could not be read; see below. */
  readError: string | null
}

/** `GET /api/status/syncs` — freshness of the platform's scheduled jobs. */
interface SyncStatus {
  id: string
  label: string
  impact: string
  lastRun: string | null
  lastLine: string | null
  staleAfterHours: number
  stale: boolean
  readError: string | null
}

/** `GET /api/status/credentials` — Entra app-registration credential expiry. */
interface AppCredential {
  appId: string
  appName: string
  credentialType: 'secret' | 'certificate'
  name: string
  expires: string
  daysRemaining: number
  status: 'expired' | 'critical' | 'warning' | 'ok'
}
interface CredentialReport {
  credentials: AppCredential[]
  readError: string | null
}

/** `GET /api/dash/cost` (`backend/src/routes/dashboards.ts`). Postgres
 *  `numeric`/`bigint` columns come back as strings over JSON. */
interface CostByUser {
  user: string
  spend: number | string
  calls: number | string
  tokens: number | string
}
interface CostData {
  byUser: CostByUser[]
  byModel: unknown[]
  daily: unknown[]
}

/** `GET /api/live?window=` — the live plane (`backend/src/routes/live.ts`). */
interface LiveTotals {
  requests: number
  activeUsers: number
  spend: number
  tokensIn: number
  tokensOut: number
  errors: number
  errorRate: number
  p95Ms: number | null
}
interface LiveModel {
  model: string
  requests: number
  spend: number
  share: number
}
interface LivePerson {
  email: string
  name: string | null
  department: string | null
  requests: number
  tokens: number
  spend: number
  topModel: string | null
  lastSeen: string | null
}
interface LiveSurface {
  apiKey: string
  requests: number
  spend: number
}
interface LiveData {
  window: string
  windowLabel: string
  source: string
  generatedAt: string
  totals: LiveTotals
  deltas: {
    requests: number | null
    activeUsers: number | null
    spend: number | null
    spendAbsolute: number | null
  }
  models: LiveModel[]
  activity: { bucketMinutes: number; points: Array<{ bucket: string; requests: number; spend: number }> }
  whoIsActive: LivePerson[]
  surfaces: LiveSurface[]
  directoryError: string | null
}

/**
 * The window options, leftmost first. The DEFAULT IS ONE HOUR, not five
 * minutes: sixty-odd people on an internal platform means a five-minute
 * window is empty most of the working day and every evening, and a
 * dashboard that reads zero by default trains you to stop looking at it.
 * 5m stays first because during an incident it is the only one that matters.
 *
 * The widest option is labelled "All time (since 11 Aug)", never "YTD" —
 * the platform is eighteen days old and YTD would imply January.
 */
const LIVE_WINDOWS: Array<{ id: string; label: string }> = [
  { id: '5m', label: '5m' },
  { id: '30m', label: '30m' },
  { id: '1h', label: '1h' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'all', label: 'All time (since 11 Aug)' },
]
const DEFAULT_LIVE_WINDOW = '1h'

/** How many days of `/api/dash/cost` back the top-spenders table — matches
 *  the Dashboards page's own default window, and is one of the backend's
 *  whitelisted `days` values (`ALLOWED_DAYS` in `routes/dashboards.ts`). */
const TOP_SPENDERS_DAYS = 30

function num(value: number | string | undefined | null): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

function money(value: number | string | undefined | null): string {
  return `$${num(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

/** 1.2M / 412k / 88. Token counts are unreadable in full. */
function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${Math.round(value / 1_000)}k`
  return String(Math.round(value))
}

/**
 * The delta hint under a live tile.
 *
 * Null means there was no prior window, or it was empty — "up from nothing"
 * has no percentage, and printing +100% there would be a made-up number. So
 * it says so rather than showing one.
 */
function pctHint(pct: number | null): string {
  if (pct === null) {
    return 'no prior window to compare'
  }
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(0)}% vs the previous window`
}

/**
 * The "Active users today" hint: the plane split when the gateway's key
 * roster was actually read, the plain window sentence otherwise.
 *
 * `keyOwnersAvailable === false` means every row on the platform read as chat
 * for this render (spendIdentity.ts's own warning on `NO_KEY_OWNERS`), so
 * printing "0 developer" then would read as nobody had touched Claude Code
 * today rather than as the gateway being briefly unreachable -- omit the
 * split entirely rather than publish a confident wrong zero.
 */
function activeUsersHint(status: StatusOverview | null): string {
  const window = status?.activeUsersWindow ?? 'today, Sydney calendar day'
  // Withhold on anything other than an explicit true -- a response from
  // before this field existed, or one where it was dropped, must read as
  // "not known to be available" rather than as a silent yes.
  if (!status || status.keyOwnersAvailable !== true) {
    return window
  }
  const chat = status.activeUsersChat ?? 0
  const dev = status.activeUsersDeveloper ?? 0
  return `${window} · ${chat} chat, ${dev} developer`
}

/** How long ago, in the terms an operator reads at a glance: 12s, 4m, 2h. */
function ago(iso: string | null): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000))
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`
  return `${Math.round(seconds / 86400)}d`
}

/**
 * A dependency-free activity sparkline.
 *
 * Recharts is already in the bundle, but this is one series of plain
 * integers with no axes, tooltip or legend — an inline SVG polyline is a
 * tenth of the code and renders identically. It carries an accessible label
 * because the shape alone is not the information.
 */
function Sparkline({ points, tokens }: { points: number[]; tokens: ThemeTokens }) {
  const width = 320
  const height = 64
  const max = Math.max(1, ...points)
  const step = points.length > 1 ? width / (points.length - 1) : width
  const path = points
    .map((value, index) => `${index === 0 ? 'M' : 'L'}${(index * step).toFixed(1)},${(height - (value / max) * (height - 4) - 2).toFixed(1)}`)
    .join(' ')
  const total = points.reduce((sum, value) => sum + value, 0)
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      role="img"
      aria-label={`${total} requests across ${points.length} buckets, peaking at ${max}`}
      style={{ marginTop: '0.5rem', display: 'block' }}
    >
      <path d={path} fill="none" stroke={tokens.brand} strokeWidth={2} strokeLinejoin="round" />
    </svg>
  )
}

/** A service is unhealthy when it isn't at its desired task count, or its
 *  current (PRIMARY) deployment has failed outright. */
function isUnhealthy(service: ServiceStatus): boolean {
  return service.running !== service.desired || service.rolloutState === 'FAILED'
}

export function OverviewPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const TABULAR: CSSProperties = { fontVariantNumeric: 'tabular-nums' }

  const [status, setStatus] = useState<StatusOverview | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)
  const [statusError, setStatusError] = useState<string | null>(null)

  const [topSpenders, setTopSpenders] = useState<CostByUser[]>([])
  const [spendersLoading, setSpendersLoading] = useState(true)
  const [spendersError, setSpendersError] = useState<string | null>(null)

  const [health, setHealth] = useState<DataHealth | null>(null)
  const [healthError, setHealthError] = useState<string | null>(null)

  const [syncs, setSyncs] = useState<SyncStatus[]>([])
  const [syncsError, setSyncsError] = useState<string | null>(null)

  const [credentials, setCredentials] = useState<CredentialReport | null>(null)
  const [credentialsError, setCredentialsError] = useState<string | null>(null)

  const [mcp, setMcp] = useState<McpTileReport | null>(null)
  const [mcpError, setMcpError] = useState<string | null>(null)
  const loadMcp = useCallback(async () => {
    setMcpError(null)
    try {
      setMcp(await apiJson<McpTileReport>('/api/status/mcp'))
    } catch (err) {
      setMcp(null)
      setMcpError(errorMessage(err))
    }
  }, [])

  const [liveWindow, setLiveWindow] = useState(DEFAULT_LIVE_WINDOW)
  const [live, setLive] = useState<LiveData | null>(null)
  const [liveLoading, setLiveLoading] = useState(true)
  const [liveError, setLiveError] = useState<string | null>(null)

  const loadLive = useCallback(async (window: string) => {
    setLiveLoading(true)
    setLiveError(null)
    try {
      setLive(await apiJson<LiveData>(`/api/live?window=${encodeURIComponent(window)}`))
    } catch (err) {
      // A failed read renders as a failure, never as an empty success: the
      // band below shows the error instead of zeroes.
      setLive(null)
      setLiveError(errorMessage(err))
    } finally {
      setLiveLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadLive(liveWindow)
  }, [loadLive, liveWindow])

  const loadSyncs = useCallback(async () => {
    setSyncsError(null)
    try {
      const data = await apiJson<{ syncs: SyncStatus[] }>('/api/status/syncs')
      setSyncs(data.syncs ?? [])
    } catch (err) {
      setSyncsError(errorMessage(err))
    }
  }, [])

  const loadCredentials = useCallback(async () => {
    setCredentialsError(null)
    try {
      setCredentials(await apiJson<CredentialReport>('/api/status/credentials'))
    } catch (err) {
      setCredentialsError(errorMessage(err))
    }
  }, [])

  const [onboardedCount, setOnboardedCount] = useState<number | null>(null)
  const loadOnboarded = useCallback(async () => {
    try {
      const data = await apiJson<{ users: unknown[] }>('/api/users/onboarded')
      setOnboardedCount(data.users.length)
    } catch {
      setOnboardedCount(null)
    }
  }, [])

  // Two queues an admin has to remember to check today. Both endpoints
  // already exist for their own pages (Budgets' request queue, Onboarding's
  // batch list) -- this reuses them rather than adding a new data source.
  // `null` means "could not be read" and renders as a dash, never a 0 that
  // looks like a genuinely empty queue.
  const [pendingBudgetRequests, setPendingBudgetRequests] = useState<number | null>(null)
  const [pendingOnboardingBatches, setPendingOnboardingBatches] = useState<number | null>(null)
  const loadPending = useCallback(async () => {
    try {
      // No `history` param is the admin queue itself (open requests only),
      // not a client-side filter over everything ever decided.
      const data = await apiJson<{ requests: unknown[] }>('/api/budget-requests')
      setPendingBudgetRequests(data.requests.length)
    } catch {
      setPendingBudgetRequests(null)
    }
    try {
      const data = await apiJson<{ batches: Array<{ status: string }> }>('/api/onboarding/batches')
      // 'pending' = created but not yet executed. 'running'/'partial'/'done'
      // have already been actioned, so they are not this tile's concern.
      setPendingOnboardingBatches(data.batches.filter((b) => b.status === 'pending').length)
    } catch {
      setPendingOnboardingBatches(null)
    }
  }, [])

  const loadHealth = useCallback(async () => {
    setHealthError(null)
    try {
      setHealth(await apiJson<DataHealth>('/api/status/data-health'))
    } catch (err) {
      setHealthError(errorMessage(err))
    }
  }, [])

  const loadStatus = useCallback(async () => {
    setStatusLoading(true)
    setStatusError(null)
    try {
      const data = await apiJson<StatusOverview>('/api/status/overview')
      setStatus(data)
    } catch (err) {
      setStatusError(errorMessage(err))
    } finally {
      setStatusLoading(false)
    }
  }, [])

  const loadTopSpenders = useCallback(async () => {
    setSpendersLoading(true)
    setSpendersError(null)
    try {
      const data = await apiJson<CostData>(`/api/dash/cost?days=${TOP_SPENDERS_DAYS}`)
      setTopSpenders(data.byUser.slice(0, 10))
    } catch (err) {
      setSpendersError(errorMessage(err))
    } finally {
      setSpendersLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadStatus()
    void loadTopSpenders()
    void loadHealth()
    void loadOnboarded()
    void loadSyncs()
    void loadCredentials()
    void loadPending()
    void loadMcp()
  }, [loadStatus, loadTopSpenders, loadHealth, loadOnboarded, loadSyncs, loadCredentials, loadPending, loadMcp])

  // The denominator, and the rule it enforces. `services.length` is what the
  // panel could DESCRIBE; `serviceCount` is what `ListServices` said the
  // cluster HAS. They were 10 and 26 for the life of this page, and nothing
  // on screen said so. Now a mismatch is red text, not silence.
  const servicesRead = status?.services.length ?? 0
  const serviceTotal = status?.serviceCount ?? servicesRead
  const listTruncated = status ? status.listComplete === false || servicesRead < serviceTotal : false

  const unhealthyCount = status?.services.filter(isUnhealthy).length ?? 0
  const alarmCount = status?.alarms.length ?? 0
  const staleSyncCount = syncs.filter((s) => s.stale).length
  // Only amber-or-worse credentials are counted: an "ok" one is not news.
  const expiringCount = (credentials?.credentials ?? []).filter((c) => c.status !== 'ok').length

  const activeColumns: Array<DataTableColumn<LivePerson>> = [
    {
      key: 'person',
      header: 'Person',
      render: (row) => (
        <span>
          {/* The email is the fallback, not a second column: the directory
              is a nightly table and can legitimately not know someone yet. */}
          {row.name ?? row.email}
          {row.name ? <div style={{ fontSize: '0.72rem', color: tokens.textSecondary }}>{row.email}</div> : null}
        </span>
      ),
    },
    { key: 'dept', header: 'Department', render: (row) => row.department ?? '—' },
    { key: 'model', header: 'Model', render: (row) => row.topModel ?? '—' },
    {
      key: 'requests',
      header: 'Req',
      align: 'right',
      render: (row) => <span style={TABULAR}>{row.requests.toLocaleString('en-AU')}</span>,
    },
    {
      key: 'tokens',
      header: 'Tokens',
      align: 'right',
      render: (row) => <span style={TABULAR}>{compact(row.tokens)}</span>,
    },
    {
      key: 'spend',
      header: 'Spend',
      align: 'right',
      render: (row) => <span style={TABULAR}>{money(row.spend)}</span>,
    },
    {
      key: 'lastSeen',
      header: 'Last seen',
      align: 'right',
      render: (row) => <span style={TABULAR}>{ago(row.lastSeen)}</span>,
    },
  ]

  const spenderColumns: Array<DataTableColumn<CostByUser>> = [
    { key: 'user', header: 'User', render: (row) => row.user },
    { key: 'spend', header: 'Spend', render: (row) => <span style={TABULAR}>{money(row.spend)}</span>, align: 'right' },
    { key: 'calls', header: 'Calls', render: (row) => <span style={TABULAR}>{num(row.calls).toLocaleString('en-AU')}</span>, align: 'right' },
  ]

  const mcpColumns: Array<DataTableColumn<McpTileServer>> = [
    { key: 'server', header: 'Server', render: (row) => row.displayName },
    { key: 'calls', header: 'Calls', render: (row) => <span style={TABULAR}>{row.calls.toLocaleString('en-AU')}</span>, align: 'right' },
    {
      key: 'denials',
      header: 'Denials',
      align: 'right',
      render: (row) => (
        <span style={{ ...TABULAR, color: row.denials > 0 ? tokens.danger : undefined, fontWeight: row.denials > 0 ? 700 : undefined }}>
          {row.denials.toLocaleString('en-AU')}
        </span>
      ),
    },
  ]

  return (
    <div style={styles.page}>
      <div style={styles.statsRow}>
        <StatCard
          label="Onboarded users"
          value={<span style={TABULAR}>{onboardedCount ?? '—'}</span>}
          tokens={tokens}
        />
        <StatCard
          label="ECS services"
          value={
            <span style={TABULAR}>
              {servicesRead} / {serviceTotal}
            </span>
          }
          hint={
            listTruncated
              ? `LIST TRUNCATED — ${servicesRead} of ${serviceTotal} read`
              : `${servicesRead} of ${serviceTotal} services read`
          }
          tokens={tokens}
          tone={listTruncated ? 'danger' : 'default'}
        />
        <StatCard
          label="Unhealthy services"
          value={<span style={TABULAR}>{unhealthyCount}</span>}
          tokens={tokens}
          tone={unhealthyCount > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Active alarms"
          value={<span style={TABULAR}>{alarmCount}</span>}
          tokens={tokens}
          tone={alarmCount > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Active users today"
          value={<span style={TABULAR}>{status?.activeUsers ?? 0}</span>}
          hint={activeUsersHint(status)}
          tokens={tokens}
        />
        <StatCard
          label="Stale scheduled jobs"
          value={<span style={TABULAR}>{staleSyncCount}</span>}
          tokens={tokens}
          tone={staleSyncCount > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Credentials expiring"
          value={<span style={TABULAR}>{credentials?.readError ? '?' : expiringCount}</span>}
          hint={credentials?.readError ? 'cannot read' : 'within 90 days'}
          tokens={tokens}
          tone={credentials?.readError || expiringCount > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Pending budget requests"
          value={<span style={TABULAR}>{pendingBudgetRequests ?? '—'}</span>}
          hint="Budgets → Requests"
          tokens={tokens}
          tone={(pendingBudgetRequests ?? 0) > 0 ? 'danger' : 'default'}
        />
        <StatCard
          label="Onboarding batches awaiting run"
          value={<span style={TABULAR}>{pendingOnboardingBatches ?? '—'}</span>}
          hint="created, not yet executed"
          tokens={tokens}
          tone={(pendingOnboardingBatches ?? 0) > 0 ? 'danger' : 'default'}
        />
      </div>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Today's platform spend</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadStatus()} disabled={statusLoading}>
            {statusLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          The platform gateway key's spend today against its ceiling — this key's daily spend IS the platform's
          whole-of-day stop-loss.
        </p>
        {statusError ? <p style={styles.error}>{statusError}</p> : null}
        {statusLoading ? (
          <p style={styles.hint}>Loading platform status…</p>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <BurnBar spend={status?.todaySpend ?? 0} cap={status?.platformCeiling ?? null} tokens={tokens} />
            <span style={{ ...styles.hint, ...TABULAR }}>
              {money(status?.todaySpend ?? 0)}
              {status?.platformCeiling != null ? ` of $${status.platformCeiling.toLocaleString()} ceiling` : ''}
            </span>
          </div>
        )}
      </section>

      {/*
        BAND 2 — the live plane. Everything here honours the window filter,
        and everything here comes from LiteLLM_SpendLogs, the only
        per-request record the platform has.

        What it deliberately cannot answer: WHAT people are doing. Topic and
        feature exist only in the nightly export, so a topic mix under a
        "last 5 minutes" heading would be a fabrication. Those stay on the
        daily plane below, with their own "as at" stamp and no re-scoping.
      */}
      <section style={styles.section} aria-label="Live activity">
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Live activity</h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => void loadLive(liveWindow)}
            disabled={liveLoading}
          >
            {liveLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Source: <code>LiteLLM_SpendLogs</code>, one row per gateway request, read now. Deltas compare against the
          immediately preceding window of the same length.
          {live?.generatedAt ? ` As at ${new Date(live.generatedAt).toLocaleTimeString('en-AU')}.` : ''}
        </p>

        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {LIVE_WINDOWS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={liveWindow === option.id}
              onClick={() => setLiveWindow(option.id)}
              style={{
                padding: '0.35rem 0.8rem',
                borderRadius: '9999px',
                border: `1px solid ${liveWindow === option.id ? tokens.brand : tokens.border}`,
                background: liveWindow === option.id ? tokens.brand : 'transparent',
                color: liveWindow === option.id ? tokens.brandContrast : tokens.textSecondary,
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        {liveError ? (
          <p style={styles.error}>
            The live plane could not be read, so these figures are unknown rather than zero: {liveError}
          </p>
        ) : null}
        {liveLoading && !live ? <p style={styles.hint}>Loading live activity…</p> : null}

        {live ? (
          <>
            <div style={styles.statsRow}>
              <StatCard
                label="Requests"
                value={<span style={TABULAR}>{live.totals.requests.toLocaleString('en-AU')}</span>}
                hint={pctHint(live.deltas.requests)}
                tokens={tokens}
              />
              <StatCard
                label="Active users"
                value={<span style={TABULAR}>{live.totals.activeUsers.toLocaleString('en-AU')}</span>}
                hint={pctHint(live.deltas.activeUsers)}
                tokens={tokens}
              />
              <StatCard
                label="Spend"
                value={<span style={TABULAR}>{money(live.totals.spend)}</span>}
                hint={pctHint(live.deltas.spend)}
                tokens={tokens}
              />
              <StatCard
                label="Tokens in / out"
                value={
                  <span style={TABULAR}>
                    {compact(live.totals.tokensIn)} / {compact(live.totals.tokensOut)}
                  </span>
                }
                tokens={tokens}
              />
              <StatCard
                label="p95 latency"
                value={
                  <span style={TABULAR}>
                    {live.totals.p95Ms === null ? '—' : `${(live.totals.p95Ms / 1000).toFixed(1)} s`}
                  </span>
                }
                hint="request_duration_ms"
                tokens={tokens}
              />
              <StatCard
                label="Errors"
                value={<span style={TABULAR}>{live.totals.errorRate.toFixed(1)}%</span>}
                hint={`${live.totals.errors.toLocaleString('en-AU')} not 'success'`}
                tokens={tokens}
                tone={live.totals.errorRate > 5 ? 'danger' : 'default'}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(20rem, 1fr))',
                gap: '1rem',
                marginTop: '1.2rem',
              }}
            >
              <div>
                <div style={styles.fieldLabel}>
                  Activity · {live.activity.bucketMinutes} min buckets
                </div>
                {live.activity.points.length === 0 ? (
                  <p style={styles.hint}>No requests in this window.</p>
                ) : (
                  <Sparkline points={live.activity.points.map((p) => p.requests)} tokens={tokens} />
                )}
              </div>

              <div>
                <div style={styles.fieldLabel}>Model mix</div>
                {live.models.length === 0 ? (
                  <p style={styles.hint}>No requests in this window.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '0.4rem' }}>
                    {live.models.slice(0, 6).map((model) => (
                      <div key={model.model} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ ...styles.hint, minWidth: '11rem', color: tokens.textPrimary }}>
                          {model.model}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            height: '0.5rem',
                            borderRadius: '9999px',
                            background: tokens.surfaceRaised,
                            overflow: 'hidden',
                          }}
                        >
                          <span
                            style={{
                              display: 'block',
                              width: `${Math.max(2, model.share)}%`,
                              height: '100%',
                              background: tokens.brand,
                            }}
                          />
                        </span>
                        <span style={{ ...styles.hint, ...TABULAR, minWidth: '3rem', textAlign: 'right' }}>
                          {model.share.toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ marginTop: '1.4rem' }}>
              <div style={styles.fieldLabel}>Who is active</div>
              {live.directoryError ? (
                <p style={styles.hint}>
                  Names and departments could not be resolved, so this table shows emails:{' '}
                  {live.directoryError}
                </p>
              ) : null}
              <p style={styles.hint}>
                Department is joined in the BFF from <code>user_directory</code>: it is in a different database, and
                Postgres has no cross-database query.
              </p>
              <DataTable
                columns={activeColumns}
                rows={live.whoIsActive.slice(0, 12)}
                getRowKey={(row) => row.email}
                getRowLabel={(row) => row.name ?? row.email}
                tokens={tokens}
                emptyLabel="Nobody has used the platform in this window."
              />
            </div>

            {/* The surface split: the only way to see the Teams bot and the
                developer API plane separately from the web app. Key hashes
                are truncated — enough to tell two surfaces apart, never
                enough to be a credential. */}
            <div style={{ marginTop: '1.4rem' }}>
              <div style={styles.fieldLabel}>Surface split · by gateway key</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.4rem' }}>
                {live.surfaces.length === 0 ? (
                  <span style={styles.hint}>No requests in this window.</span>
                ) : (
                  live.surfaces.slice(0, 8).map((surface) => (
                    <span key={surface.apiKey} style={{ ...styles.chip, ...TABULAR }}>
                      <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                        {surface.apiKey}
                      </span>
                      <span style={styles.hint}>
                        {surface.requests.toLocaleString('en-AU')} req · {money(surface.spend)}
                      </span>
                    </span>
                  ))
                )}
              </div>
            </div>
          </>
        ) : null}
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Service health</h2>
        <p style={styles.sectionSubtitle}>
          Every ECS service on the chu-gpt cluster, running vs desired task count. A service reading anything other
          than "running = desired" or mid-rollout is worth a look. Source: ECS ListServices +
          DescribeServices on cluster chu-gpt, read now.
        </p>
        {/* The denominator, stated beside the list it qualifies. ListServices
            pages at 10; this page read one page for its whole life and showed
            a green all-clear over sixteen unread services. A future regression
            has to say so here rather than hide in the code. */}
        {status ? (
          <p
            style={{
              ...styles.hint,
              color: listTruncated ? tokens.danger : tokens.textSecondary,
              fontWeight: listTruncated ? 700 : 400,
              margin: '0 0 0.8rem',
            }}
          >
            {listTruncated
              ? `⚠ LIST TRUNCATED — read ${servicesRead} of ${serviceTotal} services. This list is incomplete, so nothing below is a clean bill of health.${status.listError ? ` (${status.listError})` : ''}`
              : `${servicesRead} of ${serviceTotal} services read — list complete.`}
          </p>
        ) : null}
        {statusLoading ? (
          <p style={styles.hint}>Loading service health…</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            {(status?.services ?? []).map((service) => (
              <StatCard
                key={service.service}
                label={service.service}
                value={<span style={TABULAR}>{`${service.running}/${service.desired}`}</span>}
                // Image tag beside the rollout state, as the CLI printed it:
                // with immutable tags it is the only way to tell which build
                // is actually live after a deploy.
                hint={`${service.rolloutState ?? 'steady state'}${service.image ? ` · ${service.image}` : ''}`}
                tokens={tokens}
                tone={isUnhealthy(service) ? 'danger' : 'default'}
              />
            ))}
          </div>
        )}
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Alarms</h2>
        <p style={styles.sectionSubtitle}>CloudWatch alarms currently in ALARM state on the chu-gpt-alerts topic.</p>
        {status && status.alarms.length === 0 ? (
          <p style={styles.hint}>No active alarms.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {(status?.alarms ?? []).map((alarm) => (
              <div
                key={alarm.name}
                style={{
                  border: `1px solid ${tokens.border}`,
                  borderLeft: `3px solid ${tokens.danger}`,
                  borderRadius: '0.6rem',
                  background: tokens.surfaceRaised,
                  padding: '0.7rem 0.9rem',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: tokens.textPrimary, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem' }}>
                    {alarm.name}
                  </span>
                  {alarm.since ? (
                    <span style={styles.hint}>since {new Date(alarm.since).toLocaleString('en-AU')}</span>
                  ) : null}
                </div>
                {alarm.reason ? (
                  <div style={{ ...styles.hint, marginTop: '0.3rem', color: tokens.textSecondary }}>{alarm.reason}</div>
                ) : null}
                {alarm.metric ? (
                  <div style={styles.hint}>
                    metric: {alarm.namespace ? `${alarm.namespace} / ` : ''}
                    {alarm.metric}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Data health — semantic layer</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadHealth()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          {/* The detector was tightened to rate(1 hour) on 29 Aug
              (terraform/svc-schema-drift.tf) when the self-heal landed; this
              string was the only place in the repo still saying 6 hours. */}
          DWH views missing from the live semantic layer, per the schema-drift detector (runs hourly). A
          missing view means a query or MCP tool that depends on it returns nothing. This shows the last
          detector RUN, which can lag the live catalog by up to an hour.
          {health?.lastCheck ? ` Last checked ${new Date(health.lastCheck).toLocaleString('en-AU')}.` : ''}
          {health?.stale ? ' ⚠ The drift check has not run in 24h.' : ''}
        </p>
        {healthError ? <p style={styles.error}>{healthError}</p> : null}
        {/* An empty view list means "nothing missing" ONLY when the detector's
            log was actually read. Without this branch a permissions failure
            renders as a green tick, which is the worst outcome the panel can
            produce: a false all-clear on the data layer. */}
        {health?.readError ? (
          <p style={styles.error}>
            Cannot read the drift detector's output, so the view list below is unknown, not
            empty: {health.readError}
          </p>
        ) : null}
        {!health ? (
          <p style={styles.hint}>Loading…</p>
        ) : health.readError ? null : health.missingViews.length === 0 ? (
          <p style={{ ...styles.hint, color: tokens.brand }}>All expected views present. ✓</p>
        ) : (
          <>
            <p style={{ ...styles.error, fontWeight: 700 }}>{health.missingViews.length} view(s) missing</p>
            <ul style={{ margin: '0.3rem 0 0.8rem', padding: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {health.missingViews.map((v) => (
                <li
                  key={v}
                  style={{
                    ...styles.badge,
                    background: tokens.danger,
                    color: tokens.dangerContrast,
                    textTransform: 'none',
                    letterSpacing: 'normal',
                    fontWeight: 600,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {v}
                </li>
              ))}
            </ul>
            <div style={styles.callout}>
              <div>
                <strong>How to restore:</strong> these are FDW-dependent views a DWH re-baseline dropped. The foreign
                tables come back with the re-baseline; only the views on top are lost. Re-apply the consolidated
                restore as master (idempotent), then the drift check clears on its next run:
              </div>
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.82rem', marginTop: '0.4rem' }}>
                scripts/dwh-psql.sh master -1 -f dwh/semantic-views-025-restore-fdw-dependent-views.sql
              </div>
            </div>
            {health.unexpectedViews.length > 0 ? (
              <p style={styles.hint}>
                Unexpected (live but not in the DDL): {health.unexpectedViews.join(', ')}
              </p>
            ) : null}
          </>
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Scheduled jobs</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadSyncs()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          When each background job last ran. Every one of these fails by going quiet, so the reading
          that matters is the timestamp, not an error. A job the panel cannot read is shown as stale,
          never as healthy.
        </p>
        {syncsError ? <p style={styles.error}>{syncsError}</p> : null}
        {syncs.length === 0 && !syncsError ? <p style={styles.hint}>Loading…</p> : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {syncs.map((sync) => (
            <div
              key={sync.id}
              style={{
                border: `1px solid ${tokens.border}`,
                borderLeft: `3px solid ${sync.stale ? tokens.danger : tokens.brand}`,
                borderRadius: '0.6rem',
                background: tokens.surfaceRaised,
                padding: '0.7rem 0.9rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: tokens.textPrimary }}>{sync.label}</span>
                <span style={{ ...styles.hint, ...TABULAR }}>
                  {sync.lastRun
                    ? `last run ${new Date(sync.lastRun).toLocaleString('en-AU')}`
                    : `no run in the last ${sync.staleAfterHours}h`}
                </span>
              </div>
              {sync.stale ? (
                <div style={{ ...styles.hint, color: tokens.danger, marginTop: '0.3rem' }}>
                  {sync.readError ?? `Stale. ${sync.impact}`}
                </div>
              ) : null}
              {sync.lastLine ? (
                <div
                  style={{
                    ...styles.hint,
                    marginTop: '0.3rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                >
                  {sync.lastLine}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Credential expiry</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadCredentials()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Every dated credential on the platform's Entra app registrations, soonest first. Amber
          inside 90 days, red inside 30. A lapsed client secret is a platform-wide sign-in outage or
          a fail-closed access check, and the CLI only ever checked one registration, only when
          somebody remembered to look.
        </p>
        {credentialsError ? <p style={styles.error}>{credentialsError}</p> : null}
        {credentials?.readError ? (
          <p style={styles.error}>
            Cannot read app registrations, so this list is unknown rather than empty:{' '}
            {credentials.readError}. The panel's app registration most likely needs delegated
            Application.Read.All with admin consent.
          </p>
        ) : null}
        {!credentials && !credentialsError ? <p style={styles.hint}>Loading…</p> : null}
        {credentials && !credentials.readError && credentials.credentials.length === 0 ? (
          <p style={styles.hint}>No dated credentials found on any platform app registration.</p>
        ) : null}
        {credentials && !credentials.readError && credentials.credentials.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {credentials.credentials.map((cred) => (
              <div
                key={`${cred.appId}-${cred.credentialType}-${cred.name}-${cred.expires}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.6rem',
                  flexWrap: 'wrap',
                  border: `1px solid ${tokens.border}`,
                  borderLeft: `3px solid ${cred.status === 'ok' ? tokens.border : tokens.danger}`,
                  borderRadius: '0.6rem',
                  background: tokens.surfaceRaised,
                  padding: '0.55rem 0.9rem',
                }}
              >
                <span style={{ color: tokens.textPrimary, fontWeight: 600 }}>
                  {cred.appName}{' '}
                  <span style={styles.hint}>
                    {cred.credentialType} · {cred.name}
                  </span>
                </span>
                <span style={{ ...styles.hint, ...TABULAR }}>
                  {cred.daysRemaining < 0
                    ? `EXPIRED ${Math.abs(cred.daysRemaining)} days ago`
                    : `${cred.daysRemaining} days`}{' '}
                  · {new Date(cred.expires).toLocaleDateString('en-AU')}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Top spenders</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadTopSpenders()} disabled={spendersLoading}>
            {spendersLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {/* Round 13 correction: this was labelled as the nightly warehouse
            (`daily_user_model_spend`) but `loadTopSpenders` calls
            `/api/dash/cost`, which reads `LiteLLM_SpendLogs` directly
            (`routes/dashboards.ts`) — a per-request source, not a daily one.
            It still does not follow the live window picker above: that
            picker drives `/api/live`'s rolling minute windows, and this tile
            has its own fixed `TOP_SPENDERS_DAYS`-day range, a different
            control on the same live table, not a different plane. */}
        <p style={styles.sectionSubtitle}>
          Highest gateway spend by user, last {TOP_SPENDERS_DAYS} days. This reads{' '}
          <code>LiteLLM_SpendLogs</code> directly (per request, via <code>/api/dash/cost</code>) and does NOT
          follow the live window picker above, which drives a separate query on its own rolling window.
        </p>
        {spendersError ? <p style={styles.error}>{spendersError}</p> : null}
        {spendersLoading ? (
          <p style={styles.hint}>Loading top spenders…</p>
        ) : (
          <DataTable
            columns={spenderColumns}
            rows={topSpenders}
            getRowKey={(row) => row.user}
            getRowLabel={(row) => row.user}
            tokens={tokens}
            emptyLabel="No spend recorded in this window."
          />
        )}
      </section>

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>MCP servers, last {mcp?.windowDays ?? 7} days</h2>
          <button type="button" style={styles.buttonGhost} onClick={() => void loadMcp()}>
            Refresh
          </button>
        </div>
        <p style={styles.sectionSubtitle}>
          Reads the same nightly warehouse tables as the full MCP board, never CloudWatch or Mongo from this page
          load. Denials here are ACCESS DENIALS AT THE INGRESS: a query the SQL
          gate itself rejects never reaches the emitter that records this, so it is not counted here at all. See the{' '}
          <a href="/board?view=usage&tab=servers" style={{ color: tokens.brand }}>
            full MCP board
          </a>{' '}
          for every server, every tool, and the denials timeline.
        </p>
        {mcpError ? <p style={styles.error}>{mcpError}</p> : null}
        {mcp && !mcp.available ? (
          <p style={styles.hint}>
            MCP usage tables could not be read (not yet migrated, an ungranted role, or the database is
            unreachable) — this is "not known", not "no servers today".
          </p>
        ) : null}
        {mcp?.available ? (
          <>
            {mcp.denialsCapped ? (
              <p style={styles.hint}>
                The per-server denial split below may undercount: this window's denial list hit its own cap. The
                total below does not — it is read off every decision row, not the capped list.
              </p>
            ) : null}
            <p style={styles.sectionSubtitle}>
              {mcp.totalCalls.toLocaleString('en-AU')} total calls, {mcp.totalDenials.toLocaleString('en-AU')} total
              denials across every governed MCP server this window.
            </p>
            <DataTable
              columns={mcpColumns}
              rows={mcp.servers}
              getRowKey={(row) => row.server}
              getRowLabel={(row) => row.displayName}
              tokens={tokens}
              emptyLabel="No MCP tool calls recorded in this window."
            />
          </>
        ) : null}
      </section>
    </div>
  )
}

export default OverviewPage
